import { RoundingCards } from "@swipesense/shared_components_v2/dist/index"
import React, { useState, useEffect, useRef } from "react"
import moment from "moment"
import Loading from "../Loading"
import { Row, Col } from "reactstrap"
import Clock from "../Clock"
import RoundingMonitorLogo from "../Logo/RoundingMonitorLogo"
import PropTypes from "prop-types"
import "./RoundingMonitor.scss"
import { evaluateRoundingStatus } from "../../helpers/sharedHelperFunctions"
import { ExclamationTriangle } from "react-bootstrap-icons"

const RoundingMonitorCard = ({
  unitName,
  facilityName,
  locations,
  loading,
}) => {
  const processLocations = locations => {
    if (!locations) return []
    return locations.map(location => {
      const formattedDurationFromLastRound = lastRoundedTime => {
        if (!lastRoundedTime)
          return (
            <div className="alert-icon">
              <ExclamationTriangle />
            </div>
          )

        const timeSinceRounded = moment.duration(moment().diff(lastRoundedTime))

        const hours = timeSinceRounded.hours()
        const minutes = timeSinceRounded.minutes()

        if (hours === 0) {
          return `${minutes}m ago`
        } else if (minutes === 0) {
          return `${hours}h ago`
        } else {
          return `${hours}h ${minutes}m ago`
        }
      }

      const formattedTimestampFromLastRound = lastRoundedTime => {
        if (!lastRoundedTime) return null
        return new Date(lastRoundedTime).toLocaleString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })
      }

      return {
        ...location,
        durationTimestamp: formattedDurationFromLastRound(location.lastRounded),
        roundingStatus: evaluateRoundingStatus(location),
        roundingTimestamp: formattedTimestampFromLastRound(
          location.lastRounded
        ),
      }
    })
  }

  const sortFunc = (a, b) => {
    if (a.lastRounded > b.lastRounded || !b.lastRounded) return 1
    if (a.lastRounded < b.lastRounded || !a.lastRounded) return -1
    return 0
  }

  const [processedLocations, setProcessedLocations] = useState(
    processLocations(locations)
      .sort(sortFunc)
      .slice(0, 21)
  )
  const locationsRef = useRef(null)

  /* istanbul ignore next */
  useEffect(() => {
    const intervals = setInterval(() => {
      window.location.reload(true)
    }, 3600000)
    return () => clearInterval(intervals)
  }, [])

  useEffect(() => {
    setProcessedLocations(
      processLocations(locations)
        .sort(sortFunc)
        .slice(0, 21)
    )
  }, [locations])

  useEffect(() => {
    clearInterval(locationsRef.current)
    locationsRef.current = setInterval(() => {
      setProcessedLocations(processLocations(processedLocations).sort(sortFunc))
    }, 10000)

    return () => clearInterval(locationsRef.current)
  }, [processedLocations])

  const RoundingCardsWrapper = ({ passedLocations }) => {
    return passedLocations.map(location => {
      return (
        <div
          key={location.id}
          className="margin-right-md margin-left-md margin-bottom-tv"
        >
          <RoundingCards
            className={location.roundingStatus}
            lastRounded={location.durationTimestamp}
            lastRoundedTime={location.roundingTimestamp}
            roomNumber={location.name}
          />
        </div>
      )
    })
  }

  return (
    <div className="tv-styling content">
      <div className="rounding-monitor">
        <Row className="margin-left-md mb-5">
          <Col className="mt-5">
            <div className="pl-0 white-text margin-bottom">
              <div className="tv-font-lg text-align-left text-align-center-mobile bold unit">
                Unit: {unitName}
              </div>
              <div className="tv-font-md text-align-left text-align-center-mobile facility">
                {facilityName}
              </div>
            </div>
          </Col>
          <Col className="text-align-right">
            <RoundingMonitorLogo />
          </Col>
        </Row>
        {loading ? (
          <Loading />
        ) : (
          <>
            <Row className="row-margin-right">
              <Col xl="4" lg="12">
                <p className="white-text section-header bold float-left margin-right-md margin-left-md">
                  Room
                </p>
                <p className="white-text section-header bold float-right margin-right-md margin-left-md">
                  Last Rounded
                </p>
              </Col>
              <Col xl="4" lg="12">
                {locations.length > 7 && (
                  <>
                    <p className="white-text section-header bold float-left margin-right-md margin-left-md hidden-md">
                      Room
                    </p>
                    <p className="white-text section-header bold float-right margin-right-md margin-left-md hidden-md">
                      Last Rounded
                    </p>
                  </>
                )}
              </Col>
              <Col xl="4" lg="12">
                {locations.length > 14 && (
                  <>
                    <p className="white-text section-header bold float-left margin-right-md margin-left-md hidden-md">
                      Room
                    </p>
                    <p className="white-text section-header bold float-right margin-right-md margin-left-md hidden-md">
                      Last Rounded
                    </p>
                  </>
                )}
              </Col>
            </Row>
            <Row className="row-margin-right">
              <Col xl="4" lg="12">
                <RoundingCardsWrapper
                  passedLocations={processedLocations.slice(0, 7)}
                />
              </Col>
              <Col xl="4" lg="12">
                {locations.length > 7 && (
                  <>
                    <RoundingCardsWrapper
                      passedLocations={processedLocations.slice(7, 14)}
                    />
                  </>
                )}{" "}
              </Col>
              <Col xl="4" lg="12">
                {locations.length > 14 && (
                  <>
                    <RoundingCardsWrapper
                      passedLocations={processedLocations.slice(14, 21)}
                    />
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col xl="12" className="display-flex-start">
                {processedLocations.length > 0 &&
                  processedLocations[0].lastRounded == null && (
                    <p className="alert-icon-disclaimer white-text text-extra-large margin-left-md bold time-letter-spacing">
                      <ExclamationTriangle /> New patient hasn’t been rounded
                      yet, or hardware may be disconnected.
                    </p>
                  )}
              </Col>
              <Col lg="12">
                <Clock />
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  )
}

RoundingMonitorCard.propTypes = {
  unitName: PropTypes.string.isRequired,
  facilityName: PropTypes.string.isRequired,
  locations: PropTypes.array.isRequired,
  loading: PropTypes.bool,
}

RoundingMonitorCard.defaultProps = {
  unitName: "",
  facilityName: "",
  locations: [],
}

export default RoundingMonitorCard
